import { css } from '@emotion/react';
import { IColors } from '../../types';
import { ECalendarMode } from '../types';
import { getGridStepSize, HOUR_GUTTER_OFFSET_PX, oneHourHeight, oneHourWidth } from '../utils/timeUtils';
import { EMPTY_SLOT_HEIGHT } from '../utils/consts';

export const horizontalHourHeight = 38;
export const verticalSpaceHeight = 88;
export const DEFAULT_SPACE_HEIGHT = 110;
export const DEFAULT_SPACE_WIDTH = 154;
export const MIN_SPACE_HEIGHT = 28;
export const MIN_SPACE_WIDTH = 39;
export let spaceHeight = DEFAULT_SPACE_HEIGHT;
export let spaceWidth = DEFAULT_SPACE_WIDTH;
export const weekdayHeight = 69;
export const timeBoxWidth = 52;

/** Phones only; matches the apps' own mobile breakpoint so CSS and JS cannot disagree. */
export const PHONE_ONLY = '@media (max-width: 767px)';

const TIME_GRID_OVERLAY_Z_INDEX = 1;
const TIME_GRID_EVENTS_Z_INDEX = 2;
const TIME_GRID_LINE_THICKNESS_PX = 1;
const HOUR_BOX_TOP_MARGIN_PX = 1;

export const setSpaceHeight = (height = DEFAULT_SPACE_HEIGHT) => {
	spaceHeight = Math.max(height, MIN_SPACE_HEIGHT);
};
export const setSpaceWidth = (width = DEFAULT_SPACE_WIDTH) => {
	spaceWidth = Math.max(width, MIN_SPACE_WIDTH);
};

export const collisionGroupContainer = (top = 0, height = 0) => css`
	display: inline-flex;
	align-items: flex-start;
	width: 100%;
	position: absolute;
	top: ${top}px;
	height: ${height}px;
`;

export const collisionGroupContainerHorizontal = (left = 0, width = 0) => css`
	display: inline-flex;
	align-items: flex-start;
	height: 100%;
	position: absolute;
	left: ${left}px;
	width: ${width}px;
`;

export const draggableContainer = (top: number = 0, collisionCount: number = 1, shift: number = 0) => {
	let width = 100 / collisionCount;
	if (collisionCount >= 4) {
		width = 100 / (collisionCount + 1);
	}

	// if (width < 25) width = 25;

	let left = width * shift;
	if (left > 75) left = 75;

	return css`
		width: ${width}%;
		left: ${left}%;
		top: ${top + 1}px;
		z-index: 20;
		position: absolute;
		box-sizing: border-box;
		overflow: hidden;

		-webkit-touch-callout: none;
		-webkit-user-select: none;
		-khtml-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		user-select: none;

		&:hover {
			svg {
				opacity: 1;
			}
		}
	`;
};

export const draggableContainerHorizontal = (left: number = 0, collisionCount: number = 1, shift: number = 0) => {
	let height = 100 / collisionCount;
	if (height < 25) height = 25;

	let top = height * shift;
	if (top > 75) top = 75;

	return css`
		height: ${height}%;
		top: ${top}%;
		left: ${left}px;
		position: absolute;
		box-sizing: border-box;
		overflow: hidden;

		-webkit-touch-callout: none;
		-webkit-user-select: none;
		-khtml-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		user-select: none;
	`;
};

export const draggableContent = (height: number = 1) => css`
	height: ${height}px;
	box-sizing: border-box;
	overflow: hidden;

	> [data-slot-root] {
		height: 100%;
	}
`;

export const draggableContentHorizontal = (width: number = 1) => css`
	width: ${width - 1}px;
	box-sizing: border-box;
	height: 100%;
	overflow: hidden;

	> [data-slot-root] {
		height: 100%;
		width: 100%;
	}

	&:hover {
		svg {
			opacity: 1;
		}
	}
`;

export const draggableHandler = css`
	position: absolute;
	top: 4px;
	right: 0px;
	width: 20px;
	cursor: grab;
	overflow: hidden;

	svg {
		opacity: 0;
		transition: opacity 200ms;
	}
`;

// TODO: change to new resize icon
export const draggableResizeHandler = (cursor: string = 'ns-resize') => css`
	&:hover {
		cursor: ${cursor} !important;
	}
	position: absolute;
	display: inline-block;
	bottom: 0;
	right: 0;
	margin: 0;
	height: 14px;
	svg {
		height: 14px;
		width: 14px;
		opacity: 0;
		transition: opacity 200ms;
	}
`;

export const hourBoxContainer = (colors: IColors) => css`
	display: flex;
	position: relative;
	width: ${timeBoxWidth}px;
	box-sizing: border-box;
	-moz-box-sizing: border-box;
	-webkit-box-sizing: border-box;
	margin-top: ${HOUR_BOX_TOP_MARGIN_PX}px;

	.span {
		position: absolute;
		width: 100%;
		height: 1px;
		top: calc(50% + ${HOUR_GUTTER_OFFSET_PX}px);
		right: 0;
		transform: translate(0, -50%);
		display: block;
		background: ${colors.bg_border_seperator};
	}
	> span:first-of-type {
		position: absolute;
		width: 30%;
		height: 1px;
		top: ${HOUR_GUTTER_OFFSET_PX}px;
		right: 0;
		transform: translate(0, -50%);
		display: block;
		background: ${colors.bg_border_seperator};
	}
	div:last-of-type {
		font-family: Montserrat;
		font-style: normal;
		font-weight: normal;
		font-size: 10px;
		line-height: 12px;
		text-align: center;
		color: ${colors.tx_text_primary};
		padding-bottom: ${oneHourHeight - 13}px;
	}
`;

export const hourBoxLine = (width: number = 1000, marginTop: number = 0) => css`
	background: #0e4874;
	height: 1px;
	width: ${width}px;
	position: absolute;
	margin: 0;
	padding: 0;
	margin-top: ${marginTop}px;
	z-index: 1;

	&::after {
		display: block;
		content: '';
		width: 5px;
		height: 5px;
		border-radius: 100px;
		left: 0;
		top: 0;
		transform: translate(-50%, -40%);
		background: #0e4874;
	}
`;

export const hourBoxLineHorizontal = (width = 1000, marginLeft = 0) => css`
	background: #0e4874;
	width: 1px;
	height: ${width}px;
	position: absolute;
	margin: 0;
	padding: 0;
	margin-left: ${marginLeft}px;
	z-index: 1;
	top: 26px;

	&::after {
		display: block;
		content: '';
		width: 5px;
		height: 5px;
		border-radius: 100px;
		left: 0;
		top: 0;
		transform: translate(-40%, -40%);
		background: #0e4874;
	}
`;
export const hourBoxLineHorizontal2 = (marginLeft = 0) => css`
	background: #0e4874;
	width: 1px;
	height: calc(100% + 8px);
	position: absolute;
	margin: 0;
	padding: 0;
	left: ${marginLeft}px;
	z-index: 20;
	top: -2px;
`;

export const hourBoxLine2 = (width = 0, marginTop = 0) => css`
	background: #0e4874;
	height: 1px;
	width: max(100%, ${width}px);
	position: absolute;
	margin: 0;
	padding: 0;
	top: ${marginTop + HOUR_BOX_TOP_MARGIN_PX}px;
	z-index: 20;
	left: 0;
`;

export const hourBoxContainerHorizontal = (colors: IColors) => css`
	position: relative;
	box-sizing: border-box;
	height: ${horizontalHourHeight}px;
	min-width: ${oneHourWidth}px;

	font-family: Montserrat;
	font-style: normal;
	font-weight: normal;
	font-size: 10px;
	line-height: 12px;
	color: ${colors.tx_text_primary};

	.time {
		position: absolute;
		width: fit-content;
		left: -15px;
	}

	&:first-of-type {
		.time {
			left: 0;
		}
	}

	.small,
	.big {
		position: absolute;
		bottom: 0;
		left: 0;
		display: inline-block;
		width: 1px;
		height: 12px;
		background: ${colors.bg_border_seperator};
	}
	.big {
		left: 50%;
		height: 100%;
	}
`;

export const horizontalDayToDropContainer = (daysCount = 1, colors: IColors) => css`
	border-bottom: 1px solid ${colors.bg_border_seperator};

	/**
	 * A phone timeline row is about a hundred pixels wide, which is not enough for a time range, a sport
	 * glyph, a title and two status badges without everything truncating and overlapping. The chip keeps the
	 * title and how it is paid; the time is already on the axis above and the rest is on the slot itself.
	 * The slot body sets its rows' display inline, which only an important rule can override.
	 */
	${PHONE_ONLY} {
		.slot-time,
		.slot-context,
		.slot-frequency,
		.slot-sport,
		.slot-approval,
		.slot-conflict {
			display: none !important;
		}
	}

	height: 100%;
	min-height: ${spaceHeight}px;
	&:last-of-type {
		min-height: ${spaceHeight + 1}px;
	}
	&:first-of-type {
		min-height: ${spaceHeight}px;
	}
	box-sizing: border-box;
	min-width: ${oneHourWidth * 24 * daysCount}px;
	position: relative;
`;

const WEEKLY_HORIZONTAL_DAY_COLUMNS = 7;

export const weekBoxContainer = (colors: IColors, width: number, horizontal?: boolean) => css`
	position: relative;
	width: ${horizontal ? '100%' : `${timeBoxWidth}px`};
	box-sizing: border-box;
	display: ${horizontal ? 'grid' : 'flex'};
	${horizontal
		? `grid-template-columns: repeat(${WEEKLY_HORIZONTAL_DAY_COLUMNS}, minmax(0, 1fr));`
		: `flex-direction: column;`}
	flex: 1 1 1px;

	font-family: Montserrat;
	font-style: normal;
	font-weight: normal;
	font-size: 12px;
	line-height: 14.63px;
	text-align: center;

	&:last-of-type {
		&::after {
			height: 1px;
			width: ${width}px;
			position: absolute;
			content: '';
			bottom: -1px;
			left: 0;
			background-color: ${colors.bg_border_seperator};
			z-index: 1;
		}
	}

	.weekday {
		position: relative;
		display: flex;
		flex-direction: ${horizontal ? 'row' : 'column'};
		align-items: ${horizontal ? 'center' : 'flex-start'};
		justify-content: space-between;
		min-width: 0;

		${horizontal
			? `border-right: 1px solid ${colors.bg_border_seperator};`
			: `border-top: 1px solid ${colors.bg_border_seperator};`}

		${!horizontal && `flex: 1 1 ${weekdayHeight}px;`}
		padding: ${horizontal ? '0 12px 0 10px' : '1.5vh 0'};
		height: ${horizontal ? '40px' : `100%`};
		${!horizontal && `min-height: max(14.2857%, ${weekdayHeight}px);`}
		box-sizing: border-box;

		&::after {
			position: absolute;
			content: '';
			background-color: ${colors.bg_border_seperator};
			${horizontal
				? `
            width: 1px;
            top: 100%;
            right: -1px;  
        `
				: `
            height: 1px;
            width: ${width}px;
            top: -1px;
            left: 0;
            z-index: 1;
        `}
		}

		.name {
			width: 30px;
			text-transform: uppercase;
			color: ${colors.bg_text_secondary};
		}
		.date {
			color: ${colors.bg_text_primary};
			display: flex;
			align-items: center;
			justify-content: center;
			width: 28px;
			height: 28px;
			border-radius: 50%;
			&.today {
				color: white;
				background: linear-gradient(270deg, ${colors.bg_system_gradient} 0%, ${colors.bg_system_gradient} 100%);
			}
		}
	}
`;

export const weekDayToDropContainer = (colors: IColors, withoutChildren?: boolean) => css`
	box-sizing: border-box;
	flex-grow: 1;
	position: relative;
	border-right: 1px solid ${colors.bg_border_seperator};
	min-width: ${spaceWidth}px;
	width: ${spaceWidth}px;
	&:last-of-type {
		width: ${spaceWidth + 1}px;
	}
	height: calc(100% - 2px);
	.WeeklyDroppable {
		position: relative;
		&::after {
			content: '';
			display: block;
			height: 1px;
			background-color: ${colors.bg_border_seperator};
		}
	}
`;

export type TimeGridAxis = 'vertical' | 'horizontal';

/** Visual gridline overlay; vertical lines align to hour-gutter centers. */
export const getDayTimeGridOverlayStyle = (
	colors: IColors,
	hourSize: number,
	gridLineStepMinutes: number,
	axis: TimeGridAxis
) => {
	const stepPx = getGridStepSize(hourSize, gridLineStepMinutes);
	const vertical = axis === 'vertical';
	const gutterAlignedPosition = HOUR_GUTTER_OFFSET_PX + HOUR_BOX_TOP_MARGIN_PX - TIME_GRID_LINE_THICKNESS_PX / 2;
	return css`
		position: absolute;
		inset: 0;
		pointer-events: none;
		z-index: ${TIME_GRID_OVERLAY_Z_INDEX};
		background-image: repeating-linear-gradient(
			${vertical ? 'to bottom' : 'to right'},
			${colors.bg_border_seperator} 0,
			${colors.bg_border_seperator} ${TIME_GRID_LINE_THICKNESS_PX}px,
			transparent ${TIME_GRID_LINE_THICKNESS_PX}px,
			transparent ${stepPx}px
		);
		background-position: ${vertical ? `0 ${gutterAlignedPosition}px` : '0 0'};
	`;
};

/** Events layer above gridlines; empty cells stay clickable for add-slot. */
export const dayEventsLayerCss = css`
	position: absolute;
	inset: 0;
	z-index: ${TIME_GRID_EVENTS_Z_INDEX};
	pointer-events: none;
	.Droppable,
	.WeeklyDroppable {
		height: 100%;
		width: 100%;
		pointer-events: none;
	}
	.Droppable > *,
	.WeeklyDroppable > * {
		pointer-events: auto;
	}
`;

export const weekDayToDropHorizontalContainer = (colors: IColors) => css`
	display: grid;
	grid-template-columns: repeat(${WEEKLY_HORIZONTAL_DAY_COLUMNS}, minmax(0, 1fr));
	width: 100%;
	flex-grow: 1;
	position: relative;
	height: 100%;
	min-height: ${spaceHeight}px;
	box-sizing: border-box;

	&:last-of-type {
		min-height: ${spaceHeight + 1}px;
	}

	.WeeklyDroppable {
		min-width: 0;
		width: 100%;
		max-width: 100%;
		overflow: hidden;
	}

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		height: 1px;
		width: 100%;
		background-color: ${colors.bg_border_seperator};
	}
`;

export const monthlyViewContainer = css`
	display: flex;
	flex-wrap: wrap;
	min-width: max(100%, ${spaceWidth * 7}px);
	position: relative;
	margin-bottom: 6px;
`;

export const weekDayContainer = (colors: IColors, horizontal?: boolean, height?: string | number) => css`
	height: ${height || '100%'};
	position: relative;
	padding-top: 1px;
	box-sizing: border-box;
	min-width: 0;
	${!horizontal && `flex-grow: 1; flex-basis: 100%;`}
	${horizontal && `width: 100%; border-right: 1px solid ${colors.bg_border_seperator};`}
`;

export const weekDayEventContainer = css`
	margin: 2px 0;
	height: 16px;
	overflow: hidden;
	max-width: 100%;

	&:first-of-type {
		margin-top: 1px;
	}
`;

export const daiyHourSkeleton = css`
	height: 12px;
	overflow: hidden;
	max-width: 100%;
`;

export const moreItemsButton = (colors: IColors, isWeekly?: boolean) => css`
	position: absolute;
	right: 0;
	width: 25%;
	z-index: 20;
	cursor: pointer;
	height: 100%;
	background-color: white;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	border: 1px solid ${colors.bg_border_seperator};
	border-radius: 2px;

	${isWeekly &&
	`
    position: static;
    height: 18px;
    width: 100%;
    flex-direction: row;
    div {
      padding: 0 2px;
    }
  `}

	font-family: Montserrat;
	font-style: normal;
	font-weight: 400;
	font-size: 10px;
	line-height: 12px;
	text-align: center;
	color: ${colors.tx_text_primary};
`;

export const moreItemsButtonHorizontal = (colors: IColors, isWeekly?: boolean) => css`
	${moreItemsButton(colors, isWeekly)}
	width: 100%;
	height: 25%;
	bottom: 0;
`;

export const moreModal = (colors: IColors, position = { y: 'top: 0;', x: 'left: 50%;' }) => css`
	width: 50%;
	min-width: 232px;
	position: absolute;
	${position.y}
	${position.x}
  padding: 14px;
	background-color: white;
	border: 1px solid ${colors.bg_border_seperator};
	box-shadow: 0px 2px 22px rgba(21, 93, 148, 0.0749563);
	z-index: 100;

	.more-modal-header {
		display: flex;
		justify-content: flex-end;
		margin-bottom: 8px;
		div {
			svg {
				position: static;
				width: 20px;
				transform: none;
			}
			&:hover {
				cursor: pointer;
			}
		}
	}

	.events-list {
		max-height: 300px;
		overflow-y: auto;
		overflow-x: hidden;
	}
`;

export const monthlyDatesHeaderContainer = (colors: IColors) => css`
	display: flex;
	background-color: ${colors.bg_background_primary};
	color: ${colors.bg_text_secondary};
	font-family: Montserrat;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 15px;
	text-align: center;
	text-transform: uppercase;
	position: absolute;
	bottom: 0;
	min-width: 100%;

	div {
		padding: 5px 0;
		flex: 1 1 1px;
		min-width: max(100% / 7, ${spaceWidth}px);
		box-sizing: border-box;
		border-left: 1px solid transparent;
		border-right: 1px solid ${colors.bg_border_seperator};
		border-bottom: 1px solid ${colors.bg_border_seperator};
		&:last-of-type {
			border-right: 1px solid transparent;
		}
	}
`;

export const monthlyDateContainer = (
	colors: IColors,
	isCurrentMonth?: boolean,
	isToday?: boolean,
	isSeventh?: boolean
) => css`
	box-sizing: border-box;
	width: 100%;
	height: 100%;
	border: 1px solid ${colors.bg_border_seperator};
	border-top-color: transparent;
	border-left-color: transparent;
	${isSeventh && 'border-right-color: transparent;'}

	.date {
		font-family: Montserrat;
		font-style: normal;
		font-weight: 400;
		font-size: 10px;
		line-height: 9px;
		color: ${isCurrentMonth ? colors.bg_text_primary : colors.bg_text_teriaty};

		margin: 0 auto;
		width: 16px;
		height: 16px;
		border-radius: 50%;

		display: flex;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;

		${isToday &&
		` color: white; 
      background: linear-gradient( 270deg, ${colors.bg_system_gradient} 0%, ${colors.bg_system_gradient} 100% ); 
    `}
	}
`;

export const asideContainer = (mode = ECalendarMode.DAILY, horizontal = false) => {
	const isVerticalView = (mode === ECalendarMode.DAILY || mode === ECalendarMode.DAYX) && !horizontal;

	return css`
		overflow: hidden;
		margin-bottom: ${isVerticalView ? 12 : 0}px;
	`;
};

export const loadingContainerCss = css`
	.skeleton {
		margin-bottom: 2px;
	}
`;

export const loadingDailyContainer = css`
	.skeleton {
		margin-bottom: 2px;
	}
`;
